import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import {
	DocumentArrowDownIcon,
	FunnelIcon,
	CreditCardIcon,
	ArrowsUpDownIcon,
} from '@heroicons/react/20/solid';

import { ContainerFull } from '../../components/ContainerFull';
import { Heading } from '../../components/Heading';
import { Wrapper } from '../../components/Wrapper';
import { Button } from '../../components/buttons/Button';
import { PopoverCheckboxFilter } from '../../components/popoverCheckboxFilter/PopoverCheckboxFilter';
import { PopoverRangeDate } from '../../components/popoverRangeDate/PopoverRangeDate';
import { PopoverRadioFilter } from '../../components/popoverRadioFilter/PopoverRadioFilter';
import { SmartTable } from '../../components/smartTable/SmartTable';
import { Tooltip } from '../../components/smartTable/Tooltip';
import { useTablePrefs } from '../../hooks/useTablePrefs';

import { getAllPayments, cleanActionSelectedPayment } from '../../redux/actions/payment';
import { verifyStateApp } from '../../redux/actions/stateApp';
import { exportToExcel } from '../../common/importDataToExcel';
import { optionsStatusPayment, optionsSort, paymentTypes } from '../../static/data';

import { DrawerPaymentValidation } from './DrawerPaymentValidation';
import { DrawerProfileUser } from '../profile/drawerProfileUser/DrawerProfileUser';
import { DrawerFiltersPayments } from './DrawerFiltersPayments';
import { buildPaymentsColumns } from './payments.columns';
import { getOrderedExcelHeadersForExport } from '../../common/getOrderedExcelHeadersForExport';

const TABLE_KEY = 'pagos';

const EMPTY_FILTERS = {
	status: [],
	paymentType: [],
	startDate: '',
	endDate: '',
};

const DEFAULT_PREFS = {
	query: {
		page: 0,
		pageSize: 15,
		sorting: [{ id: 'formatCreatedAt', desc: true }],
		filters: { ...EMPTY_FILTERS },
		columnSearch: {},
	},
	view: {
		columnVisibility: {},
		columnOrder: [],
		columnSizing: {},
	},
};

const PAYMENT_TYPE_FILTER_OPTIONS = paymentTypes
	.filter((p) => p.id !== 'allTypes')
	.map((p) => ({
		value: p.description,
		label: p.name,
		description: p.description,
	}));

const PaymentsFilterBarSkeleton = () => (
	<div className='hidden w-full flex-wrap items-center justify-end gap-3 md:flex md:flex-1'>
		{[1, 2, 3, 4, 5].map((i) => (
			<div
				key={i}
				className='h-10 w-44 max-w-[40vw] shrink-0 rounded-md bg-slate-200/90 animate-blink'
				aria-hidden
			/>
		))}
	</div>
);

const sortingToApi = (sorting) => {
	if (!Array.isArray(sorting) || sorting.length === 0) {
		return { field: 'formatCreatedAt', dir: 'desc' };
	}
	const [first] = sorting;
	return { field: first.id, dir: first.desc ? 'desc' : 'asc' };
};

const buildApiPayload = (query) => ({
	page: query.page,
	pageSize: query.pageSize,
	filters: query.filters || {},
	columnSearch: query.columnSearch || {},
	sort: sortingToApi(query.sorting),
	usePagination: true,
});

export const PaymentsPage = () => {
	const dispatch = useDispatch();
	const { payments, loadingPayments } = useSelector((state) => state.payment);
	const { user } = useSelector((state) => state.user);
	const { typeUser } = user;
	const typeUserCanValidate = ['director', 'cobranza', 'ventas'];

	const [prefs, , patchPrefs, resetPrefs, prefsHydrated] = useTablePrefs(TABLE_KEY, DEFAULT_PREFS);

	const [totalRows, setTotalRows] = useState(0);
	const [idPayment, setIdPayment] = useState('');
	const [isOpenDrawerPaymentValidation, setIsOpenDrawerPaymentValidation] = useState(false);
	const [isOpenDrawerProfile, setIsOpenDrawerProfile] = useState(false);
	const [profileUserId, setProfileUserId] = useState('');
	const [isOpenFiltersDrawer, setIsOpenFiltersDrawer] = useState(false);
	const [exportExcelLoading, setExportExcelLoading] = useState(false);
	const [mobileSortDesc, setMobileSortDesc] = useState(true);

	const [formData, setFormData] = useState(() => ({ ...EMPTY_FILTERS, ...prefs.query.filters }));
	const [mobileFormData, setMobileFormData] = useState(formData);

	const filtersSyncKey = JSON.stringify(prefs.query.filters ?? {});
	useEffect(() => {
		setFormData({ ...EMPTY_FILTERS, ...prefs.query.filters });
	}, [filtersSyncKey]);

	const patchQuery = useCallback(
		(patch) => {
			patchPrefs({ query: { ...prefs.query, ...patch } });
		},
		[patchPrefs, prefs.query],
	);

	const patchView = useCallback(
		(patch) => {
			patchPrefs({ view: { ...prefs.view, ...patch } });
		},
		[patchPrefs, prefs.view],
	);

	const handleColumnSearchChange = useCallback(
		(columnId, value) => {
			const current = prefs.query.columnSearch || {};
			const trimmed = (value || '').trim();
			const next = { ...current };
			if (trimmed) {
				if (current[columnId] === trimmed) return;
				next[columnId] = trimmed;
			} else {
				if (!(columnId in current)) return;
				delete next[columnId];
			}
			patchQuery({ page: 0, columnSearch: next });
		},
		[patchQuery, prefs.query.columnSearch],
	);

	useEffect(() => {
		dispatch(cleanActionSelectedPayment());
	}, [dispatch]);

	const queryKey = JSON.stringify(prefs.query);
	useEffect(() => {
		if (!prefsHydrated) return;
		dispatch(getAllPayments(buildApiPayload(prefs.query))).then((result) => {
			if (result?.status === 200) {
				setTotalRows(result.totalRows || 0);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryKey, dispatch, prefsHydrated]);

	const applyFilters = useCallback(
		(nextFormData) => {
			const data = nextFormData || formData;
			patchQuery({
				page: 0,
				filters: {
					status: data.status || [],
					paymentType: data.paymentType || [],
					startDate: data.startDate || '',
					endDate: data.endDate || '',
				},
			});
		},
		[patchQuery, formData],
	);

	const removeFilters = useCallback(() => {
		setFormData({ ...EMPTY_FILTERS });
		patchQuery({
			page: 0,
			filters: { ...EMPTY_FILTERS },
			columnSearch: {},
			sorting: [{ id: 'formatCreatedAt', desc: true }],
		});
	}, [patchQuery]);

	const toggleFiltersDrawer = useCallback(() => {
		setIsOpenFiltersDrawer((prev) => {
			const next = !prev;
			if (next) {
				setMobileFormData(formData);
				const desc = prefs.query.sorting?.[0]?.desc ?? true;
				setMobileSortDesc(desc);
			}
			return next;
		});
	}, [formData, prefs.query.sorting]);

	const applyMobileFilters = useCallback(() => {
		setFormData(mobileFormData);
		patchQuery({
			page: 0,
			filters: {
				status: mobileFormData.status || [],
				paymentType: mobileFormData.paymentType || [],
				startDate: mobileFormData.startDate || '',
				endDate: mobileFormData.endDate || '',
			},
			sorting: [{ id: 'formatCreatedAt', desc: mobileSortDesc }],
		});
		setIsOpenFiltersDrawer(false);
	}, [mobileFormData, mobileSortDesc, patchQuery]);

	const clearMobileFilters = useCallback(() => {
		setMobileFormData({ ...EMPTY_FILTERS });
		setMobileSortDesc(true);
		removeFilters();
	}, [removeFilters]);

	const currentSortValue = (prefs.query.sorting?.[0]?.desc ?? true) ? 'desc' : 'asc';
	const handleSortChange = useCallback(
		(value) => {
			patchQuery({
				page: 0,
				sorting: [{ id: 'formatCreatedAt', desc: value === 'desc' }],
			});
		},
		[patchQuery],
	);

	const handleOpenDrawerPaymentValidation = useCallback((paymentId) => {
		setIdPayment(paymentId);
		setIsOpenDrawerPaymentValidation(true);
	}, []);

	const toggleDrawerPaymentValidation = useCallback(() => {
		setIsOpenDrawerPaymentValidation((prev) => {
			if (prev) setIdPayment('');
			return !prev;
		});
	}, []);

	const openDrawerProfile = useCallback((userId) => {
		if (!userId) return;
		setProfileUserId(userId);
		setIsOpenDrawerProfile(true);
	}, []);

	const toggleDrawerProfile = useCallback(() => {
		setIsOpenDrawerProfile((prev) => {
			if (prev) setProfileUserId('');
			return !prev;
		});
	}, []);

	const refreshTable = useCallback(() => {
		dispatch(getAllPayments(buildApiPayload(prefs.query))).then((result) => {
			if (result?.status === 200) setTotalRows(result.totalRows || 0);
		});
	}, [dispatch, prefs.query]);

	const columns = useMemo(
		() =>
			buildPaymentsColumns({
				typeUser,
				typeUserCanValidate,
				onOpenValidation: handleOpenDrawerPaymentValidation,
				onOpenProfile: openDrawerProfile,
			}),
		[typeUser, handleOpenDrawerPaymentValidation, openDrawerProfile],
	);

	const handleImportData = async (name) => {
		const stateAppRes = await dispatch(verifyStateApp());
		if (stateAppRes?.data === false) {
			toast.error(stateAppRes.message);
			return;
		}
		const excelColumns = getOrderedExcelHeadersForExport(columns, prefs.view);
		if (excelColumns.length === 0) {
			toast.error(
				'No hay columnas de datos para exportar. Muestra al menos una columna exportable en la tabla.',
			);
			return;
		}

		setExportExcelLoading(true);
		const loadingToastId = toast.loading('Generando...');
		try {
			const result = await dispatch(
				getAllPayments({
					...buildApiPayload(prefs.query),
					usePagination: false,
					sendExcel: true,
					excelColumns,
				}),
			);
			if (result?.status === 200) {
				const response = exportToExcel(result.excelData, name);
				if (response.data) {
					toast.error(response.message);
				} else {
					toast.success(response.message);
				}
			} else {
				toast.error(result?.message || 'No se pudo generar el archivo Excel.');
			}
		} catch {
			toast.error('Error al generar el archivo Excel.');
		} finally {
			toast.dismiss(loadingToastId);
			setExportExcelLoading(false);
		}
	};

	const rows = payments;

	return (
		<ContainerFull>
			<Heading
				title='Historial de pagos'
				subtitle='Muestra el historial de pagos realizados por los alumnos; puedes validar los pagos pendientes o ver el detalle.'
				center={false}
			/>

			<div className='mt-4 flex w-full flex-col items-center justify-end gap-4 md:flex-row'>
				<div className='flex w-full md:hidden'>
					<Button
						onClick={toggleFiltersDrawer}
						disabled={!prefsHydrated}
						icon={<FunnelIcon className='block h-5 w-5' aria-hidden='true' />}
						variant='barFilterSecondary'
						className='w-full'
					>
						Filtros
					</Button>
				</div>

				{prefsHydrated ? (
					<>
						<div className='hidden w-full md:flex md:w-auto'>
							<Tooltip title='Orden por fecha de registro del pago' placement='top'>
								<div className='w-full md:w-auto'>
									<PopoverRadioFilter
										value={currentSortValue}
										onChange={handleSortChange}
										options={optionsSort}
										title='Orden por fecha de registro'
										subtitle='Más reciente o más antiguo según la fecha en que se registró el pago.'
										icon={ArrowsUpDownIcon}
										placeholderLabel='Ordenar por'
									/>
								</div>
							</Tooltip>
						</div>

						<div className='hidden w-full md:flex md:w-auto'>
							<Tooltip title='Filtrar por rango de fechas de registro del pago' placement='top'>
								<div className='w-full md:w-auto'>
									<PopoverRangeDate
										state={formData}
										setState={setFormData}
										properties={['startDate', 'endDate']}
										popoverTitle='Fecha de registro del pago'
										popoverSubtitle='Filtra por la fecha en que se registró el pago en el sistema.'
										emptySummaryLabel='Fechas de registro'
										showMonthYearSelects
										handleApllyFilters={applyFilters}
										handleRemoveFilter={removeFilters}
									/>
								</div>
							</Tooltip>
						</div>

						<div className='hidden w-full md:flex md:w-auto'>
							<Tooltip title='Estatus del pago' placement='top'>
								<div className='w-full md:w-auto'>
									<PopoverCheckboxFilter
										state={formData}
										setState={setFormData}
										property='status'
										options={optionsStatusPayment}
										title='Estatus del pago'
										subtitle='Selecciona uno o varios estatus para filtrar los pagos.'
										icon={CreditCardIcon}
										placeholderLabel='Estatus del pago'
										handleApllyFilters={applyFilters}
										handleRemoveFilter={removeFilters}
									/>
								</div>
							</Tooltip>
						</div>

						<div className='hidden w-full md:flex md:w-auto'>
							<Tooltip title='Tipo de pago' placement='top'>
								<div className='w-full md:w-auto'>
									<PopoverCheckboxFilter
										state={formData}
										setState={setFormData}
										property='paymentType'
										options={PAYMENT_TYPE_FILTER_OPTIONS}
										title='Tipo de pago'
										subtitle='Suscripción y/o pre registro.'
										icon={CreditCardIcon}
										placeholderLabel='Tipo de pago'
										handleApllyFilters={applyFilters}
										handleRemoveFilter={removeFilters}
									/>
								</div>
							</Tooltip>
						</div>
					</>
				) : (
					<PaymentsFilterBarSkeleton />
				)}

				<div className='flex w-full flex-col items-stretch gap-1 md:w-auto md:items-end'>
					<Tooltip
						title={exportExcelLoading ? 'Generando archivo Excel…' : 'Descargar archivo de Excel'}
						placement='top'
					>
						<Button
							onClick={() => handleImportData('Lista_de_pagos.xlsx')}
							disabled={!prefsHydrated || exportExcelLoading}
							icon={<DocumentArrowDownIcon className='block h-5 w-5' aria-hidden='true' />}
							variant='barFilterSecondary'
							className='w-full md:w-auto'
							loading={exportExcelLoading}
							loadingLabel='Generando...'
						>
							Exportar lista
						</Button>
					</Tooltip>
				</div>
			</div>

			<Wrapper>
				<SmartTable
					data={rows}
					columns={columns}
					totalRows={totalRows}
					query={prefs.query}
					onQueryChange={patchQuery}
					onColumnSearchChange={handleColumnSearchChange}
					view={prefs.view}
					onViewChange={patchView}
					loading={loadingPayments}
					preferencesReady={prefsHydrated}
					emptyMessage='No hay registros de pagos'
					pageSizeOptions={[10, 15, 25, 50]}
					onResetPrefs={resetPrefs}
					getRowId={(row) => row.id}
				/>
			</Wrapper>

			<DrawerPaymentValidation
				isOpenDrawerFilters={isOpenDrawerPaymentValidation}
				toggleDrawerFilters={toggleDrawerPaymentValidation}
				idPayment={idPayment}
				updateDataTable={refreshTable}
			/>
			<DrawerProfileUser
				isOpenDrawerProfile={isOpenDrawerProfile}
				toggleDrawerProfile={toggleDrawerProfile}
				userId={profileUserId}
				onUserUpdated={refreshTable}
				refreshTable={refreshTable}
			/>

			<DrawerFiltersPayments
				isOpen={isOpenFiltersDrawer}
				toggleDrawer={toggleFiltersDrawer}
				formData={mobileFormData}
				setFormData={setMobileFormData}
				sortValue={mobileSortDesc ? 'desc' : 'asc'}
				onSortChange={(v) => setMobileSortDesc(v === 'desc')}
				onClear={clearMobileFilters}
				onApply={applyMobileFilters}
			/>
		</ContainerFull>
	);
};
